import { useDispatch, useSelector } from "react-redux";
import { clearConfirmTypeModal, setConfirmTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { useGetProposalRequirementQuery } from "../../api/gms/group-category/group-category-api";
import { useUpdateSECorTINProposalMutation, useUpdateStatusAsBookedMutation } from "../../api/gms/proposal/proposal-api";
import { useRef, useState } from "react";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { selectUserId } from "../../features/auth/auth-slice";
import { selectedProposalPreparedById } from "../../features/gms-state-and-modal/state-slice";
import { useProposalData } from "../populate-data/use-proposal-data";
import { useGetProposalAttachmentRequirementsStatusQuery, useLazyViewAttachmentFileQuery, useUploadRequirementFileMutation } from "../../api/gms/proposal-attachment-requirements/proposal-attachment-requirements-api";
import { useToastify } from "../../library/toastify/toast-provider";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useRequirementUpload = () => {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch()
    const { refetchProposal, populateProposalPreview } = useProposalData()
    const { currentProposalId } = useModalAccess();
    const userId = useSelector(selectUserId)
    
    const preparedById = useSelector(selectedProposalPreparedById)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [files, setFiles] = useState<Record<number, File | null>>({})
    const [savedFiles, setSavedFiles] = useState<Record<number, boolean>>({})
    const [selectedRequirementId, setSelectedRequirementId] = useState<number | null>(null)

    const { isBookedProposal, isReleaseProposal, tinNumber, secNumber } = useProposalTypeData()
    
    const { data: fileRequirement, isLoading: isLoadingFileRequirement, isError: isErrorFileRequirement, error: errorFileRequirement } = useGetProposalRequirementQuery()
    const { data: fileRequirementsStatus, isLoading: isLoadingFileRequirementsStatus, isError: isErrorFileRequirementsStatus, error: errorFileRequirementsStatus, refetch: refetchFileRequirementsStatus } = useGetProposalAttachmentRequirementsStatusQuery({ proposalID: currentProposalId! }, { skip: !currentProposalId });
    const [uploadRequirementFile, { isLoading: isLoadingUploadRequirementFile }] = useUploadRequirementFileMutation();
    const [bookedProposal, { isLoading: isLoadingBookedProposal }] = useUpdateStatusAsBookedMutation();
    const [viewFile, { isLoading: isLoadingViewFile }] = useLazyViewAttachmentFileQuery();
    const [updateTINorSEC, {isLoading: isLoadingUpdateTINorSEC}] = useUpdateSECorTINProposalMutation();
    const fileRequirementErrorMessage = getErrorMessage(errorFileRequirement);
    const fileRequirementStatusErrorMessage = getErrorMessage(errorFileRequirementsStatus);
    
    const handleShowModalConfirmBookedProposal = () => {
        if(!isReleaseProposal) return showToastNotify('This proposal must be release before it can be booked.', 'warning');

        if (!tinNumber?.trim() && !secNumber?.trim()) {
            dispatch(setConfirmTypeModal('isMissingTinAndSec'))
            return
        }

        dispatch(setConfirmTypeModal('isConfirmBookedProposal'))
    }

    const handleCloseModalConfirmBookedProposal = () => {
        dispatch(clearConfirmTypeModal())
    }

    const handleBookedProposal = async () => {
        if(!currentProposalId) return 
        if(isBookedProposal) return showToastNotify('The proposal is already marked as booked.', 'warning')
        try {
            await bookedProposal(currentProposalId).unwrap();
            dispatch(clearConfirmTypeModal())
            showToastNotify('Application successfully marked as Booked', 'success')
            await refetchProposal()
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleSaveTinOrSecNumber = async () => {

        if(!currentProposalId) return showToastNotify("No proposal selected.", "error");
        const tin = tinNumber?.trim() || "";
        const sec = secNumber?.trim() || "";
        // Require at least one
        if (!tin && !sec) {
            return showToastNotify("Please provide either a TIN or SEC number.", "warning");
        }

        // TIN validation: 000-000-000-000000
        if (tin && !/^\d{3}-\d{3}-\d{3}-\d{3,5}$/.test(tin)) {
            return showToastNotify("Invalid TIN format. Use 000-000-000-000 (last section: 3–5 digits).", "warning");
        }

        // SEC validation: minimum 7 characters
        if (sec && sec.length < 7) {
            return showToastNotify("SEC number must be at least 7 characters long.", "warning");
        }
        try {
            const response = await updateTINorSEC({ proposalId: currentProposalId, body: { company_tin: tin, company_sec: sec } }).unwrap();
            dispatch(clearConfirmTypeModal())
            populateProposalPreview();
            showToastNotify(response.message, "success");
        } catch (error) {
            showToastNotify("Failed to insert TIN or SEC number.", "error");
        }
    }

    const handleUploadClick = (requirementId: number) => {
        // if(isBooked) return showToastNotify('The proposal is already booked and cannot be modified.', 'warning')
        if(userId !== preparedById) return showToastNotify(`Only the proposal preparer can upload files.`, 'warning')
        setFiles({});
        setSavedFiles({});
        setSelectedRequirementId(requirementId);
        fileInputRef.current?.click();
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];

        if (!selectedFile || !selectedRequirementId) return;

        setFiles(prev => ({
            ...prev,
            [selectedRequirementId]: selectedFile
        }))

        // mark as unsaved whenever file changes
        setSavedFiles(prev => ({
            ...prev,
            [selectedRequirementId]: false
        }))

        e.target.value = '';
    }

    const handleSave = async (requirementId: number) => {
        const keyId = String(requirementId);
        const selectedFile = files[requirementId];

        if (!selectedFile  || !currentProposalId) return;

        if(selectedFile.type !== 'application/pdf' && !selectedFile.name.toLowerCase().endsWith(".pdf")) {
            return showToastNotify('Only PDF files are allowed.', 'warning')
        }

        try {
            await uploadRequirementFile({
                proposalID: currentProposalId,
                key: keyId,
                file: selectedFile
            }).unwrap();

            await refetchFileRequirementsStatus();
            
            setFiles(prev => {
                const updated = { ...prev };
                delete updated[requirementId];
                return updated;
            });
            showToastNotify('File uploaded successfully', 'success')
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleRemove = (requirementId: number) => {
        setFiles(prev => {
            const updated = { ...prev }
            delete updated[requirementId]
            return updated
        })

        setSavedFiles(prev => {
            const updated = { ...prev }
            delete updated[requirementId]
            return updated
        })
    }
    
    const handleViewFile = async (path: string) => {
        if(!path) return showToastNotify('Unable to view file: file path is missing.', 'warning')
        try {
            const normalizedPath = path.replace(/\\/g, '/');
            const blob = await viewFile(normalizedPath).unwrap();

            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error: any) {
            showToastNotify(`File not found on server.`, 'error')
        }
    };


    return {
        handleShowModalConfirmBookedProposal,
        handleCloseModalConfirmBookedProposal,
        handleViewFile,
        // API
        fileRequirementErrorMessage,
        fileRequirement,
        isLoadingFileRequirement,
        isErrorFileRequirement,
        errorFileRequirement,
        isLoadingViewFile,

        fileRequirementStatusErrorMessage,
        fileRequirementsStatus,
        isLoadingFileRequirementsStatus,
        isErrorFileRequirementsStatus,
        errorFileRequirementsStatus,

        isLoadingUploadRequirementFile,

        isLoadingBookedProposal,
        // STATE
        proposalId: currentProposalId,
        fileInputRef,
        files,
        savedFiles,


        // FUNCTION
        handleBookedProposal,
        handleUploadClick,
        handleFileChange,
        handleSave,
        handleRemove,

        handleSaveTinOrSecNumber,
        isLoadingUpdateTINorSEC,
    }

}